import {
  FunctionComponent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import { useSearchParams } from 'react-router-dom';
import { UserContext } from '../../context';
import {
  NotificationsTabType,
  useEventByIdQuery,
  useOrganisationQuery,
  useStoryCardByIdQuery,
} from '../../services';
import {
  Button,
  ButtonType,
  NOTIFICATIONS_ADMIN_COOKIE_KEY,
  OrderDirection,
  SearchField,
  Select,
  TabItem,
  Tabs,
  cookieOptions,
  useAppDispatch,
  useAppSelector,
  useDebounce,
} from '../../shared';
import {
  getInAppMessages,
  getPushNotifications,
  loadMoreInAppMessages,
  loadMorePushNotifications,
} from '../../slices';
import { NoContent } from '../Error';
import { CreateNotificationModal } from './CreateNotificationModal';
import { Notification } from './Notification';

import classes from './Notifications.module.scss';

export enum NotificationsType {
  PUSH = 'push',
  INAPP = 'in-app',
}

interface NotificationsProps {
  isOrgLevel?: boolean;
}

export const Notifications: FunctionComponent<NotificationsProps> = memo(({ isOrgLevel }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [searchParams] = useSearchParams();

  const { channelId } = useContext(UserContext).userInfo.userData;

  const storyId = Number(searchParams.get('storyId'));

  const cardId = Number(searchParams.get('cardId'));

  const eventId = Number(searchParams.get('eventId'));

  const { data: card } = useStoryCardByIdQuery({ storyId, cardId }, { skip: !storyId || !cardId });

  const { data: event } = useEventByIdQuery({ channelId, eventId }, { skip: !eventId });

  const { channels } = useAppSelector(({ channels }) => channels);

  const channel = channels.find(({ id }) => id === channelId);

  const { data: organisation } = useOrganisationQuery();

  const {
    pushNotifications,
    page: pushNotificationsPage,
    hasNextPage: pushNotificationsHasNextPage,
    isFetched: isFetchedPushNotifications,
  } = useAppSelector(({ pushNotifications }) => pushNotifications);

  const {
    inAppMessages,
    page: inAppMessagesPage,
    hasNextPage: inAppMessagesHasNextPage,
    isFetched: isFetchedInAppMessages,
  } = useAppSelector(({ inAppMessages }) => inAppMessages);

  const [cookies, setCookie] = useCookies([NOTIFICATIONS_ADMIN_COOKIE_KEY]);

  const { selectedTabIndex = NotificationsTabType.PUSH } = {
    ...cookies[NOTIFICATIONS_ADMIN_COOKIE_KEY],
  };

  const [createNotificationModalOpen, setCreateNotificationModalOpen] = useState<boolean>(false);

  const [query, setQuery] = useState('');

  const debouncedQuery = useDebounce<string>(query);

  const [orderDirection, setOrderDirection] = useState(OrderDirection.DESC);

  const type = useMemo(
    () =>
      selectedTabIndex === NotificationsTabType.PUSH
        ? NotificationsType.PUSH
        : NotificationsType.INAPP,
    [selectedTabIndex]
  );

  const isPush = useMemo(() => type === NotificationsType.PUSH, [type]);

  const openModalLabel = useMemo(
    () => `${t('common.new')} ${t(`notificationsPage.${type}`)}`,
    [t, type]
  );

  const channelPushNotificationAllowToCreate = useMemo(
    () => Boolean(channel?.permissions.channelPushNotificationAllowToCreate),
    [channel?.permissions.channelPushNotificationAllowToCreate]
  );

  const organisationPushNotificationAllowToCreate = useMemo(
    () => Boolean(organisation?.permissions.organisationPushNotificationAllowToCreate),
    [organisation?.permissions.organisationPushNotificationAllowToCreate]
  );

  const openModalDisabled = useMemo(
    () => !channelPushNotificationAllowToCreate && !organisationPushNotificationAllowToCreate,
    [channelPushNotificationAllowToCreate, organisationPushNotificationAllowToCreate]
  );

  const intro = useMemo(() => t(`notificationsPage.intro-${type}`), [t, type]);

  const noContentSubTitle = useMemo(() => t(`notificationsPage.no-content-${type}`), [t, type]);

  const noPushNotifications = useMemo(
    () => Boolean(isPush && isFetchedPushNotifications && !pushNotifications.length),
    [isFetchedPushNotifications, isPush, pushNotifications.length]
  );

  const noInAppMessages = useMemo(
    () => Boolean(!isPush && isFetchedInAppMessages && !inAppMessages.length),
    [inAppMessages.length, isFetchedInAppMessages, isPush]
  );

  const loader = useMemo(() => {
    return <Skeleton className={classes['notifications__loader']} height={'5rem'} count={3} />;
  }, []);

  useEffect(() => {
    if (card || event) {
      setCreateNotificationModalOpen(true);
    }
  }, [card, event]);

  useEffect(() => {
    if (isPush) {
      dispatch(
        getPushNotifications({
          ...(!isOrgLevel && { channelId }),
          filters: { query, excludeOrganisationNotifications: !isOrgLevel },
          orderDirection,
        })
      );
      return;
    }

    dispatch(
      getInAppMessages({
        ...(!isOrgLevel && { channelId }),
        filters: { query, excludeOrganisationMessages: !isOrgLevel },
        orderDirection,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, orderDirection]);

  const loadMore = useCallback(() => {
    if (isPush) {
      dispatch(
        loadMorePushNotifications({
          channelId,
          filters: { query },
          page: pushNotificationsPage + 1,
          orderDirection,
        })
      );
      return;
    }

    dispatch(
      loadMoreInAppMessages({
        channelId,
        filters: { query },
        page: inAppMessagesPage + 1,
        orderDirection,
      })
    );
  }, [
    channelId,
    dispatch,
    inAppMessagesPage,
    isPush,
    orderDirection,
    pushNotificationsPage,
    query,
  ]);

  const renderPushNotifications = useMemo(() => {
    if (!isFetchedPushNotifications) {
      return loader;
    }

    if (noPushNotifications) {
      return <NoContent subtitle={noContentSubTitle} />;
    }

    return pushNotifications.map(
      ({
        id,
        title,
        message,
        createdTime,
        deliveredTime,
        expirationTime,
        permissions: { allowToDelete },
        statistic: { open, sent },
        channelId,
        url,
        storyCard,
        event,
      }) => (
        <Notification
          key={id}
          id={id}
          isPush={true}
          title={title}
          message={message}
          created={createdTime}
          delivered={deliveredTime}
          expiration={expirationTime}
          allowToDelete={allowToDelete}
          open={open}
          sent={sent}
          channelId={channelId}
          url={url}
          storyCard={storyCard}
          event={event}
        />
      )
    );
  }, [
    isFetchedPushNotifications,
    loader,
    noContentSubTitle,
    noPushNotifications,
    pushNotifications,
  ]);

  const renderInAppMessages = useMemo(() => {
    if (!isFetchedInAppMessages) {
      return loader;
    }

    if (noInAppMessages) {
      return <NoContent subtitle={noContentSubTitle} />;
    }

    return inAppMessages.map(
      ({
        id,
        title,
        intro,
        created,
        delivery,
        expire,
        permissions: { allowToDelete },
        statistic: { open, sent },
        primary: { action },
        channelId,
        image,
        placement,
        placementPosition,
      }) => (
        <Notification
          key={id}
          id={id}
          isPush={false}
          title={title}
          message={intro}
          created={created}
          delivered={delivery}
          expiration={expire}
          allowToDelete={allowToDelete}
          open={open}
          sent={sent}
          channelId={channelId}
          url={decodeURIComponent(action.split('?url=')[1] ?? '')}
          image={image}
          placement={placement}
          placementPosition={placementPosition}
          storyCard={null}
          event={null}
        />
      )
    );
  }, [inAppMessages, isFetchedInAppMessages, loader, noContentSubTitle, noInAppMessages]);

  const tabContent = useCallback(
    (notifications: JSX.Element | JSX.Element[]) => {
      return (
        <div className={classes['notifications__content']}>
          <div className={classes['notifications__content-intro']}>{intro}</div>
          <div className={classes['notifications__content-filters']}>
            <SearchField
              value={query}
              onChange={setQuery}
              placeholder={t('common.type-to-search')}
            />
            <Select
              selectedItemId={orderDirection}
              items={[
                { id: OrderDirection.ASC, title: t('notificationsPage.order-direction-asc') },
                { id: OrderDirection.DESC, title: t('notificationsPage.order-direction-desc') },
              ]}
              onChange={(value) => setOrderDirection(value as OrderDirection)}
            />
          </div>
          <InfiniteScroll
            className={classes['notifications__content-list']}
            next={loadMore}
            hasMore={isPush ? pushNotificationsHasNextPage : inAppMessagesHasNextPage}
            dataLength={isPush ? pushNotifications.length : inAppMessages.length}
            loader={loader}
          >
            {notifications}
          </InfiniteScroll>
        </div>
      );
    },
    [
      inAppMessages.length,
      inAppMessagesHasNextPage,
      intro,
      isPush,
      loadMore,
      loader,
      orderDirection,
      pushNotifications.length,
      pushNotificationsHasNextPage,
      query,
      t,
    ]
  );

  const onTabChange = useCallback(
    (index: number) => {
      setCookie(NOTIFICATIONS_ADMIN_COOKIE_KEY, { selectedTabIndex: index }, cookieOptions());

      switch (index) {
        case NotificationsTabType.PUSH:
          return dispatch(
            getPushNotifications({
              ...(!isOrgLevel && { channelId }),
              filters: { query, excludeOrganisationNotifications: !isOrgLevel },
              orderDirection,
            })
          );
        case NotificationsTabType.INAPP:
          return dispatch(
            getInAppMessages({
              ...(!isOrgLevel && { channelId }),
              filters: { query, excludeOrganisationMessages: !isOrgLevel },
              orderDirection,
            })
          );
      }
    },
    [channelId, dispatch, isOrgLevel, orderDirection, query, setCookie]
  );

  const tabItems: TabItem[] = useMemo(
    () => [
      {
        index: NotificationsTabType.PUSH,
        name: t('notificationsPage.push'),
        content: tabContent(renderPushNotifications),
      },
      {
        index: NotificationsTabType.INAPP,
        name: t('notificationsPage.in-app'),
        content: tabContent(renderInAppMessages),
      },
    ],
    [renderInAppMessages, renderPushNotifications, t, tabContent]
  );

  return (
    <div className={classes['notifications']}>
      <div className={classes['notifications__header']}>
        <h1 className={classes['notifications__header-title']}>{t('notificationsPage.title')}</h1>
        <Button
          type={ButtonType.primary}
          label={openModalLabel}
          disabled={openModalDisabled}
          className={classes['notifications__header-button']}
          onClick={() => setCreateNotificationModalOpen(true)}
        />
      </div>

      <Tabs
        items={tabItems}
        selectedTabIndex={selectedTabIndex}
        setSelectedTabIndex={onTabChange}
        noBackground
      />

      {createNotificationModalOpen && (
        <CreateNotificationModal
          isOpen={createNotificationModalOpen}
          onClose={() => setCreateNotificationModalOpen(false)}
          type={type}
          query={query}
          orderDirection={orderDirection}
          organisationPushNotificationAllowToCreate={organisationPushNotificationAllowToCreate}
          card={card}
          event={event}
          isOrgLevel={isOrgLevel}
        />
      )}
    </div>
  );
});
